import { useCallback, useEffect, useState } from "react";

import { Alert, Button, Center, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";
import { httpsCallable } from "firebase/functions";

import { useFunctions } from "../../helpers/firebaseContext";

type EmbedUrlResponse = {
  url: string;
  expiresAt: number;
};

export default function ServiceRequestMetabaseDashboard() {
  const functions = useFunctions();
  const [embedUrl, setEmbedUrl] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const loadEmbedUrl = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const getEmbedUrl = httpsCallable<unknown, EmbedUrlResponse>(
        functions,
        "getServiceRequestDashboardEmbedUrl",
      );
      const result = await getEmbedUrl({});

      setEmbedUrl(result.data.url);
      setExpiresAt(result.data.expiresAt);
    } catch (error) {
      console.error("Failed to load service request dashboard embed URL", error);
      setErrorMessage("Unable to load the service request dashboard.");
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  useEffect(() => {
    void loadEmbedUrl();
  }, [loadEmbedUrl]);

  useEffect(() => {
    if (!expiresAt) return;

    const refreshDelay = Math.max(expiresAt - Date.now() - 60_000, 60_000);
    const refreshTimer = window.setTimeout(() => {
      void loadEmbedUrl();
    }, refreshDelay);

    return () => window.clearTimeout(refreshTimer);
  }, [expiresAt, loadEmbedUrl]);

  return (
    <Stack gap="sm" w="100%" h="100%">
      <Group justify="flex-end">
        <Button
          variant="light"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          onClick={() => void loadEmbedUrl()}
          loading={isLoading}
        >
          Refresh
        </Button>
      </Group>

      <Paper withBorder radius="md" style={{ flex: 1, minHeight: 680, overflow: "hidden" }}>
        {isLoading && !embedUrl ? (
          <Center h="100%">
            <Stack align="center" gap="sm">
              <Loader />
              <Text size="sm" c="dimmed">Loading dashboard...</Text>
            </Stack>
          </Center>
        ) : errorMessage ? (
          <Alert
            m="md"
            color="red"
            icon={<IconAlertCircle size={18} />}
            title="Dashboard unavailable"
          >
            {errorMessage}
          </Alert>
        ) : (
          <iframe
            title="Service request dashboard"
            src={embedUrl}
            sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
            referrerPolicy="no-referrer"
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              background: "#06141d",
            }}
          />
        )}
      </Paper>
    </Stack>
  );
}
